/* global google */
import { Children, useEffect, useMemo, useRef, useState } from 'react';

/**
 * SearchBox wrapper for @vis.gl/react-google-maps.
 * Requires Maps JS loaded with the "places" library.
 */
const SearchBox = ({
  children,
  bounds,
  options,
  onPlacesChanged,
  onLoad,
  onUnmount,
}) => {
  const containerRef = useRef(null);
  const [searchBox, setSearchBox] = useState(null);
  const listenersRef = useRef([]);

  const updater = useMemo(
    () => ({
      bounds: (instance, newBounds) => instance?.setBounds?.(newBounds),
    }),
    []
  );

  useEffect(() => {
    if (!google?.maps?.places) return undefined;
    const input = containerRef.current?.querySelector('input');
    if (!input) return undefined;

    const sb = new google.maps.places.SearchBox(input, options);
    setSearchBox(sb);
    if (onLoad) onLoad(sb);

    return () => {
      listenersRef.current.forEach((l) => l.remove());
      listenersRef.current = [];
      if (onUnmount) onUnmount(sb);
      setSearchBox(null);
    };
  }, [options, onLoad, onUnmount]);

  useEffect(() => {
    if (!searchBox) return;
    listenersRef.current.forEach((l) => l.remove());
    listenersRef.current = [];

    if (onPlacesChanged) {
      listenersRef.current.push(
        searchBox.addListener('places_changed', () => onPlacesChanged())
      );
    }
  }, [searchBox, onPlacesChanged]);

  useEffect(() => {
    if (!searchBox || typeof bounds === 'undefined') return;
    updater.bounds(searchBox, bounds);
  }, [searchBox, bounds, updater]);

  return <div ref={containerRef}>{Children.only(children)}</div>;
};

export default SearchBox;

